import { useContext, useMemo, useRef, useState } from "react";
import { Link2, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { checkText, suggestLinks, useInternalLinks, wrapLink, LinkSelfContext } from "@/lib/internalLinks";

const cls =
  "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]";

/**
 * Plain-text editor with internal linking: highlight words, choose an approved page,
 * and the text becomes [words](/page). Suggests links for service names and warns
 * about broken, prohibited, unpublished or repeated links. Formatting (line breaks)
 * is preserved because links are written inline.
 */
export default function LinkableTextarea({ value, onChange, rows = 4, testid, label, placeholder }) {
  const ref = useRef(null);
  const selfUrl = useContext(LinkSelfContext);
  const allTargets = useInternalLinks();
  const targets = useMemo(() => allTargets.filter((t) => t.url !== selfUrl), [allTargets, selfUrl]);
  const [pick, setPick] = useState("");
  const text = value || "";
  const issues = useMemo(() => checkText(text, allTargets), [text, allTargets]);
  const suggestions = useMemo(() => suggestLinks(text, targets).slice(0, 4), [text, targets]);

  const linkSelection = (url) => {
    const el = ref.current;
    if (!url || !el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    if (a === b || !text.slice(a, b).trim()) {
      toast.info("First highlight the words you want to turn into a link.");
      return;
    }
    if (/[[\]()]/.test(text.slice(a, b))) {
      toast.error("That selection already contains link code. Select plain words only.");
      return;
    }
    onChange(wrapLink(text, a, b, url));
  };

  return (
    <div className="block" data-testid={testid ? `${testid}-wrap` : undefined}>
      {label && <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>}
      <textarea
        ref={ref}
        data-testid={testid}
        value={text}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={cls}
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Link2 className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
        <select
          aria-label="Link highlighted text to an approved page"
          data-testid={testid ? `${testid}-link-select` : undefined}
          value={pick}
          onChange={(e) => {
            linkSelection(e.target.value);
            setPick("");
          }}
          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs"
        >
          <option value="">Link highlighted text to…</option>
          {targets.map((t) => (
            <option key={t.url} value={t.url}>
              {t.label}{t.live ? "" : " (not published)"}
            </option>
          ))}
        </select>
        <span className="text-[11px] text-slate-400">Links look like [link text](/page)</span>
      </div>
      {suggestions.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={testid ? `${testid}-suggestions` : undefined}>
          <Lightbulb className="w-3.5 h-3.5 text-amber-500" aria-hidden="true" />
          {suggestions.map((sg) => (
            <button
              type="button"
              key={sg.url}
              data-testid={testid ? `${testid}-suggest-${sg.url.replace("/", "")}` : undefined}
              onClick={() => onChange(wrapLink(text, sg.start, sg.end, sg.url))}
              className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
            >
              Link “{sg.phrase}” → {sg.url}
            </button>
          ))}
        </div>
      )}
      {issues.length > 0 && (
        <ul className="mt-1.5 space-y-0.5" data-testid={testid ? `${testid}-issues` : undefined}>
          {issues.map((i, n) => (
            <li key={n} className={`text-xs ${i.level === "error" ? "text-rose-700" : "text-amber-700"}`}>
              {i.level === "error" ? "Not allowed: " : "Heads up: "}
              {i.anchor ? `“${i.anchor}” ` : ""}
              {i.url ? `(${i.url}) ` : ""}
              {i.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
